import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bluetooth, Scan, Battery, Settings, CheckCircle, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { bluetoothScannerService, type BluetoothScannerDevice } from '../../services/bluetoothScanner';

interface BluetoothScannerSetupProps {
  onScanResult: (barcode: string) => void;
}

export const BluetoothScannerSetup: React.FC<BluetoothScannerSetupProps> = ({
  onScanResult
}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [device, setDevice] = useState<BluetoothScannerDevice | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [lastScan, setLastScan] = useState<string | null>(null);

  useEffect(() => {
    checkBluetoothSupport();
    setupScannerCallbacks();
  }, []);

  const checkBluetoothSupport = async () => {
    const supported = await bluetoothScannerService.isBluetoothSupported();
    setIsSupported(supported);
  };

  const setupScannerCallbacks = () => {
    bluetoothScannerService.onScan((barcode) => {
      setLastScan(barcode);
      onScanResult(barcode);
    });

    bluetoothScannerService.onConnectionChange((connected) => {
      setIsConnected(connected);
      if (!connected) {
        setDevice(null);
        setBatteryLevel(null);
      }
    });
  };

  const handleRequestDevice = async () => {
    try {
      const selectedDevice = await bluetoothScannerService.requestDevice();
      if (selectedDevice) {
        setDevice(selectedDevice);
      }
    } catch (error) {
      console.error('Failed to request device:', error);
    }
  };

  const handleConnect = async () => {
    if (!device) return;
    
    setIsConnecting(true);
    try {
      const success = await bluetoothScannerService.connect();
      if (success) {
        setIsConnected(true);
        // Get battery level
        const battery = await bluetoothScannerService.getBatteryLevel();
        setBatteryLevel(battery);
      }
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await bluetoothScannerService.disconnect();
    setIsConnected(false);
    setDevice(null);
    setBatteryLevel(null);
  };

  const handleTestScan = () => {
    // Simulate a scan for testing
    const testBarcodes = ['1234567890123', '9876543210987', 'TEST-SKU-001'];
    const randomBarcode = testBarcodes[Math.floor(Math.random() * testBarcodes.length)];
    bluetoothScannerService.simulateScan(randomBarcode);
    
    // Add notification for test scan
    onScanResult(randomBarcode);
  };

  if (!isSupported) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Bluetooth Not Supported
          </h3>
          <p className="text-gray-600">
            Your browser doesn't support Bluetooth Web API. Please use a compatible browser.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Bluetooth className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Bluetooth Scanner</h2>
          <p className="text-gray-600">Connect and configure your Bluetooth barcode scanner</p>
        </div>
      </div>

      {/* Connection Status */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-gray-400" />
            )}
            <Badge variant={isConnected ? 'success' : 'neutral'}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </div>
        </div>

        {device && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Device:</span>
              <span className="font-medium">{device.name}</span>
            </div>
            
            {batteryLevel !== null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Battery:</span>
                <div className="flex items-center space-x-2">
                  <Battery className="w-4 h-4 text-gray-600" />
                  <span className="font-medium">{batteryLevel}%</span>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Last Seen:</span>
              <span className="text-sm">{new Date(device.lastSeen).toLocaleString()}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Device Selection */}
      {!device && (
        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Scanner</h3>
          <p className="text-gray-600 mb-4">
            Click the button below to search for and select your Bluetooth barcode scanner.
          </p>
          
          <Button
            onClick={handleRequestDevice}
            icon={<Bluetooth className="w-4 h-4" />}
          >
            Search for Scanners
          </Button>
        </Card>
      )}

      {/* Connection Controls */}
      {device && (
        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Controls</h3>
          
          <div className="flex space-x-3">
            {!isConnected ? (
              <Button
                onClick={handleConnect}
                loading={isConnecting}
                icon={<Bluetooth className="w-4 h-4" />}
              >
                Connect
              </Button>
            ) : (
              <Button
                onClick={handleDisconnect}
                variant="outline"
                icon={<Bluetooth className="w-4 h-4" />}
              >
                Disconnect
              </Button>
            )}
            
            <Button
              onClick={handleTestScan}
              disabled={!isConnected}
              variant="scan"
              icon={<Scan className="w-4 h-4" />}
            >
              Test Scan
            </Button>
          </div>
        </Card>
      )}

      {/* Scanner Configuration */}
      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Scanner Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Sound Feedback</p>
              <p className="text-sm text-gray-600">Play beep sound on successful scan</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Vibration Feedback</p>
              <p className="text-sm text-gray-600">Vibrate device on successful scan</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Auto-Connect</p>
              <p className="text-sm text-gray-600">Automatically connect to known devices</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Recent Scans */}
      {lastScan && (
        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Scan</h3>
          
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">{lastScan}</p>
                <p className="text-sm text-gray-600">Scanned successfully</p>
              </div>
            </div>
            
            <Badge variant="success">Success</Badge>
          </div>
        </Card>
      )}

      {/* Troubleshooting */}
      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Troubleshooting</h3>
        
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium text-gray-900">Scanner not found?</p>
            <p className="text-gray-600">
              Make sure your scanner is in pairing mode and within range.
            </p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">Connection issues?</p>
            <p className="text-gray-600">
              Try turning Bluetooth off and on, then reconnect the scanner.
            </p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">Scans not working?</p>
            <p className="text-gray-600">
              Check that the scanner is configured for HID mode or keyboard emulation.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};